import type { ApprovedVoHistorySummary } from "@shared/variationOrderPresentation";
import { formatVoPlacementSuffix, voViewHref } from "@shared/variationOrderPresentation";
import { Link } from "wouter";

type Props = {
  items: ApprovedVoHistorySummary[];
  /** Staff Link A uses /sales/vo/:id/preview; customers use /vo/:publicToken. */
  staffLinks?: boolean;
  className?: string;
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });
}

function qtySuffix(q: number | null | undefined): string {
  if (q == null || !Number.isFinite(Number(q))) return "";
  return ` ×${Number(q)}`;
}

function lineLabel(l: { name: string; quantity?: number | null; systemName: string; roomName: string }): string {
  return `${l.name}${qtySuffix(l.quantity)}${formatVoPlacementSuffix(l.systemName, l.roomName)}`;
}

function LineBriefItem({
  line,
}: {
  line: {
    name: string;
    quantity?: number | null;
    systemName: string;
    roomName: string;
    changeReason?: string | null;
  };
}) {
  // `changeReason` on history briefs is already display-only (explicit override).
  const reason = String(line.changeReason ?? "").trim();
  return (
    <li>
      <span>• {lineLabel(line)}</span>
      {reason ? (
        <p className="ml-3 mt-0.5 text-xs text-[#374151]" data-testid="vo-history-change-reason">
          <span className="font-medium text-[#1A2744]">Reason: </span>
          {reason}
        </p>
      ) : null}
    </li>
  );
}

/** Concise approved-VO audit trail shown on the living proposal. */
export default function ProposalApprovedVariationOrders({ items, staffLinks = false, className }: Props) {
  if (!items.length) return null;

  return (
    <section
      className={className ?? "mb-6 rounded-xl border border-[#1A2744]/12 bg-[#FAFAF7] p-4"}
      data-testid="proposal-approved-variation-orders"
      aria-labelledby="proposal-approved-vos-heading"
    >
      <h2 id="proposal-approved-vos-heading" className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]">
        Approved Variation Orders
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        The proposal below is the current agreed contract. Variation Orders explain what changed.
      </p>
      <ul className="mt-4 space-y-4">
        {items.map((vo) => {
          const href = voViewHref({
            staffLinks,
            voId: vo.id,
            publicToken: vo.publicToken,
          });
          return (
            <li
              key={vo.id}
              className="rounded-lg border border-[#1A2744]/10 bg-white p-3"
              data-testid={`proposal-vo-history-${vo.voNumber}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-semibold text-[#1A2744]">{vo.voNumber}</p>
                <p className="text-xs text-muted-foreground">Approved · {formatDate(vo.approvedAt)}</p>
              </div>
              {String(vo.changeReason ?? "").trim() ? (
                <p className="mt-2 text-xs text-[#374151]" data-testid="vo-history-vo-change-reason">
                  <span className="font-medium text-[#1A2744]">Change reason: </span>
                  {String(vo.changeReason).trim()}
                </p>
              ) : null}
              <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                {vo.added.length > 0 ? (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[#1A2744]/55">Added</p>
                    <ul className="mt-1 space-y-0.5 text-[#1A2744]">
                      {vo.added.map((l, i) => (
                        <LineBriefItem key={`a-${i}`} line={l} />
                      ))}
                    </ul>
                  </div>
                ) : null}
                {vo.removed.length > 0 ? (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[#1A2744]/55">Removed</p>
                    <ul className="mt-1 space-y-0.5 text-[#1A2744]">
                      {vo.removed.map((l, i) => (
                        <LineBriefItem key={`r-${i}`} line={l} />
                      ))}
                    </ul>
                  </div>
                ) : null}
                {vo.quantityChanged.length > 0 ? (
                  <div className="sm:col-span-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[#1A2744]/55">
                      Quantity changes
                    </p>
                    <ul className="mt-1 space-y-0.5 text-[#1A2744]">
                      {vo.quantityChanged.map((l, i) => {
                        const reason = String(l.changeReason ?? "").trim();
                        return (
                          <li key={`q-${i}`}>
                            <span>
                              • {l.name} · {l.quantityBefore ?? "—"} → {l.quantityAfter ?? "—"}
                              {formatVoPlacementSuffix(l.systemName, l.roomName)}
                            </span>
                            {reason ? (
                              <p
                                className="ml-3 mt-0.5 text-xs text-[#374151]"
                                data-testid="vo-history-change-reason"
                              >
                                <span className="font-medium text-[#1A2744]">Reason: </span>
                                {reason}
                              </p>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
              </div>
              {href ? (
                <p className="mt-2">
                  <Link
                    href={href}
                    className="text-sm font-medium text-[#1A2744] underline-offset-2 hover:underline"
                  >
                    View Variation Order
                  </Link>
                </p>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">
                  Ask your consultant for the Variation Order document if you need the full amendment record.
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
